/**
 * DMS 90-Day Strategy Operator — Sprint 5: Manual Analytics Snapshots.
 *
 * Users log the numbers we CAN'T pull automatically: followers added, reach,
 * engagement, outcomes/wins per channel per week or month. Combined with the
 * Consistency Report (execution data) this closes the loop from "posted" to
 * "worked". No auto-imports yet — Sprint 6 territory.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Plus, BarChart3, Save, Trash2, Edit3, X,
} from "lucide-react";

const DEFAULT_CHANNELS = ["Instagram", "YouTube", "TikTok", "Email", "Blog"];


function today() { return new Date().toISOString().slice(0, 10); }

function shiftDate(iso, deltaDays) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}


export default function StrategyAnalytics() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // snapshot object or "new"
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/strategy/analytics/snapshots");
      setItems(data.items || []);
      setAccessDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) setAccessDenied(true);
      else toast.error(err?.response?.data?.detail || "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    const start = shiftDate(today(), -7);
    setEditing({
      period_type: "week",
      period_start: start,
      period_end: shiftDate(start, 6),
      channels: DEFAULT_CHANNELS.slice(0, 2).map(n => ({ name: n, followers_added: 0, reach: 0, engagement_rate: 0, outcomes: "" })),
      outcomes: "", wins: "", friction: "", next_focus: "",
    });
  };

  const submit = async (payload) => {
    setSaving(true);
    try {
      if (payload.id) {
        const { data } = await api.patch(`/strategy/analytics/snapshots/${payload.id}`, payload);
        setItems(prev => prev.map(x => x.id === data.id ? data : x));
        toast.success("Snapshot updated");
      } else {
        const { data } = await api.post("/strategy/analytics/snapshots", payload);
        setItems(prev => [data, ...prev]);
        toast.success("Snapshot saved");
      }
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try {
      await api.delete(`/strategy/analytics/snapshots/${id}`);
      setItems(prev => prev.filter(x => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-[#64748B]" data-testid="analytics-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (accessDenied) {
    return (
      <div className="max-w-3xl px-5 sm:px-6 lg:px-10 py-10" data-testid="analytics-access-denied">
        <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Strategy home
        </Link>
        <div className="dms-card p-6">
          <h1 className="text-xl font-heading font-bold text-[#0A2342]">Not available on this plan</h1>
          <p className="mt-2 text-[#475569] text-[14px]">
            Manual analytics ships with the 90-day Strategy Operator. Upgrade to unlock.
          </p>
          <Link to="/billing" className="mt-4 inline-block rounded-md bg-[#0A2342] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#152F5A]">
            View plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl px-5 sm:px-6 lg:px-10 py-6" data-testid="analytics-page">
      <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Strategy home
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="dms-label">Manual analytics · self-scored KPIs</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342] flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#1D4E89]" /> Analytics snapshots
          </h1>
          <p className="mt-2 text-[13.5px] text-[#64748B] max-w-xl">
            Log the numbers we can&apos;t pull automatically — followers added, reach,
            engagement, outcomes per channel. Snapshots feed into your weekly &amp;
            monthly review conversations with yourself.
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="new-snapshot-btn"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-4 py-2 text-[13px] font-semibold hover:bg-[#152F5A]"
        >
          <Plus className="w-4 h-4" /> New snapshot
        </button>
      </div>

      {editing && (
        <SnapshotEditor
          snapshot={editing}
          onCancel={() => setEditing(null)}
          onSubmit={submit}
          saving={saving}
        />
      )}

      <div className="mt-6 space-y-3" data-testid="snapshot-list">
        {items.length === 0 && !editing && (
          <div className="dms-card p-6 text-center text-[13.5px] text-[#64748B]">
            No snapshots yet. Log your first week to start building a trend line.
          </div>
        )}
        {items.map((s) => (
          <SnapshotRow key={s.id} s={s} onEdit={() => setEditing(s)} onDelete={() => remove(s.id)} />
        ))}
      </div>
    </div>
  );
}


function SnapshotRow({ s, onEdit, onDelete }) {
  const total = (s.channels || []).reduce((sum, c) => ({
    followers: sum.followers + Number(c.followers_added || 0),
    reach: sum.reach + Number(c.reach || 0),
  }), { followers: 0, reach: 0 });
  return (
    <div className="dms-card p-4" data-testid={`snapshot-${s.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#64748B]">
            {s.period_type} · {s.period_start} → {s.period_end}
          </div>
          <div className="text-[15px] font-heading font-bold text-[#0A2342] mt-0.5">
            {s.channels?.length || 0} channels · <span className="text-emerald-700">+{total.followers}</span> followers · <span className="text-[#1D4E89]">{total.reach.toLocaleString()}</span> reach
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={onEdit} data-testid={`edit-${s.id}`} className="p-1.5 hover:bg-[#F1F5F9] rounded text-[#334155]">
            <Edit3 className="w-4 h-4" />
          </button>
          <button onClick={onDelete} data-testid={`delete-${s.id}`} className="p-1.5 hover:bg-red-50 rounded text-red-600">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {(s.channels || []).length > 0 && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[12.5px]">
          {(s.channels || []).map((c, i) => (
            <div key={i} className="flex flex-wrap items-baseline gap-2 py-1 border-b border-[#F1F5F9]">
              <span className="font-semibold text-[#0A2342]">{c.name}</span>
              <span className="text-[#64748B]">+{c.followers_added || 0}</span>
              <span className="text-[#64748B]">{Number(c.reach || 0).toLocaleString()} reach</span>
              <span className="text-[#64748B]">{c.engagement_rate || 0}% ER</span>
              {c.outcomes && <span className="ml-auto text-[11.5px] text-[#475569] italic truncate max-w-[240px]">{c.outcomes}</span>}
            </div>
          ))}
        </div>
      )}
      {(s.wins || s.friction || s.next_focus) && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[12px]">
          {s.wins && <div className="rounded bg-emerald-50 text-emerald-800 px-2 py-1"><b>Wins:</b> {s.wins}</div>}
          {s.friction && <div className="rounded bg-orange-50 text-orange-800 px-2 py-1"><b>Friction:</b> {s.friction}</div>}
          {s.next_focus && <div className="rounded bg-[#F0FDFA] text-[#0A2342] px-2 py-1"><b>Next:</b> {s.next_focus}</div>}
        </div>
      )}
    </div>
  );
}


function SnapshotEditor({ snapshot, onCancel, onSubmit, saving }) {
  const [state, setState] = useState(snapshot);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const setPeriodType = (t) => {
    // Recompute period_end sensibly: week = start+6, month = start+29
    const days = t === "month" ? 29 : 6;
    set("period_type", t);
    setState((s) => ({ ...s, period_end: shiftDate(s.period_start, days) }));
  };
  const setChannel = (i, k, v) => {
    setState((s) => {
      const channels = [...(s.channels || [])];
      channels[i] = { ...channels[i], [k]: v };
      return { ...s, channels };
    });
  };
  const addChannel = () => {
    setState((s) => ({
      ...s,
      channels: [...(s.channels || []), { name: "", followers_added: 0, reach: 0, engagement_rate: 0, outcomes: "" }],
    }));
  };
  const removeChannel = (i) => {
    setState((s) => ({ ...s, channels: (s.channels || []).filter((_, idx) => idx !== i) }));
  };

  const canSubmit = state.period_type && state.period_start && state.period_end && (state.channels || []).some(c => c.name?.trim());

  return (
    <div className="dms-card p-5" data-testid="snapshot-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-bold text-[#0A2342]">
          {state.id ? "Edit snapshot" : "New snapshot"}
        </div>
        <button onClick={onCancel} data-testid="cancel-editor" className="p-1 hover:bg-[#F1F5F9] rounded"><X className="w-4 h-4 text-[#64748B]" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="dms-label">Period</label>
          <select
            value={state.period_type}
            onChange={(e) => setPeriodType(e.target.value)}
            data-testid="period-type"
            className="dms-input mt-1 text-[13px]"
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>
        <div>
          <label className="dms-label">Start</label>
          <input
            type="date"
            value={state.period_start}
            onChange={(e) => set("period_start", e.target.value)}
            data-testid="period-start"
            className="dms-input mt-1 text-[13px]"
          />
        </div>
        <div>
          <label className="dms-label">End</label>
          <input
            type="date"
            value={state.period_end}
            onChange={(e) => set("period_end", e.target.value)}
            data-testid="period-end"
            className="dms-input mt-1 text-[13px]"
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="dms-label">Channels ({(state.channels || []).length})</label>
          <button onClick={addChannel} type="button" data-testid="add-channel" className="text-[12px] text-[#1D4E89] hover:underline">+ Add channel</button>
        </div>
        <div className="space-y-2">
          {(state.channels || []).map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`channel-row-${i}`}>
              <input
                type="text"
                value={c.name || ""}
                onChange={(e) => setChannel(i, "name", e.target.value)}
                placeholder="Instagram"
                className="dms-input text-[13px] col-span-3"
              />
              <input
                type="number"
                value={c.followers_added || 0}
                onChange={(e) => setChannel(i, "followers_added", Number(e.target.value))}
                placeholder="+ followers"
                className="dms-input text-[13px] col-span-2"
              />
              <input
                type="number"
                value={c.reach || 0}
                onChange={(e) => setChannel(i, "reach", Number(e.target.value))}
                placeholder="reach"
                className="dms-input text-[13px] col-span-2"
              />
              <input
                type="number"
                step="0.1"
                value={c.engagement_rate || 0}
                onChange={(e) => setChannel(i, "engagement_rate", Number(e.target.value))}
                placeholder="ER %"
                className="dms-input text-[13px] col-span-2"
              />
              <input
                type="text"
                value={c.outcomes || ""}
                onChange={(e) => setChannel(i, "outcomes", e.target.value)}
                placeholder="outcomes"
                className="dms-input text-[13px] col-span-2"
              />
              <button onClick={() => removeChannel(i)} type="button" className="text-red-600 hover:bg-red-50 p-1 rounded col-span-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="dms-label">Wins</label>
          <textarea
            rows={2}
            value={state.wins || ""}
            onChange={(e) => set("wins", e.target.value)}
            className="dms-input mt-1 text-[13px]"
            placeholder="What worked?"
          />
        </div>
        <div>
          <label className="dms-label">Friction</label>
          <textarea
            rows={2}
            value={state.friction || ""}
            onChange={(e) => set("friction", e.target.value)}
            className="dms-input mt-1 text-[13px]"
            placeholder="What blocked you?"
          />
        </div>
        <div>
          <label className="dms-label">Next focus</label>
          <textarea
            rows={2}
            value={state.next_focus || ""}
            onChange={(e) => set("next_focus", e.target.value)}
            className="dms-input mt-1 text-[13px]"
            placeholder="What to double down on?"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={onCancel} type="button" className="rounded-md border border-[#E2E8F0] px-4 py-2 text-[13px] font-semibold text-[#64748B]">
          Cancel
        </button>
        <button
          onClick={() => onSubmit(state)}
          disabled={!canSubmit || saving}
          data-testid="save-snapshot"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2 text-[13px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {state.id ? "Save changes" : "Save snapshot"}
        </button>
      </div>
    </div>
  );
}
